import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Popconfirm, Row, Col, message, Spin, TimePicker, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useGroups } from '../../hooks/useGroups';
import { addGroup, updateGroup, deleteGroup } from '../../store/slices/groupsSlice';
import dayjs from 'dayjs';

const Groups = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { groups, loading, refetchGroups } = useGroups(selectedBranch?.id, selectedPlace?.id);
    
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingGroup, setEditingGroup] = useState(null);

    const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

    const columns = [
        {
            title: 'Grup Adı',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Antreman Saatleri',
            dataIndex: 'periods',
            key: 'periods',
            render: (periods) => {
                if (!periods || periods.length === 0) return '-';
                return (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {periods.map((p, idx) => (
                            <li key={idx}>
                                {dayNames[p.dayOfWeek]} - {p.startTime?.slice(0,5)} - {p.endTime?.slice(0,5)}
                            </li>
                        ))}
                    </ul>
                );
            }
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Bu grubu silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const dayOptions = [
        { label: 'Pazartesi', value: 1 },
        { label: 'Salı', value: 2 },
        { label: 'Çarşamba', value: 3 },
        { label: 'Perşembe', value: 4 },
        { label: 'Cuma', value: 5 },
        { label: 'Cumartesi', value: 6 },
        { label: 'Pazar', value: 0 },
    ];

    const handleAdd = () => {
        setEditingGroup(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingGroup(record);
        // periods varsa formata uygun şekilde set et
        form.setFieldsValue({
            ...record,
            periods: record.periods
                ? record.periods.map(p => ({
                    dayOfWeek: p.dayOfWeek,
                    startTime: dayjs(p.startTime, 'HH:mm'),
                    endTime: dayjs(p.endTime, 'HH:mm')
                }))
                : []
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await dispatch(deleteGroup(id)).unwrap();
            message.success('Grup başarıyla silindi!');
            refetchGroups();
        } catch (error) {
            console.error('Delete group error:', error);
            message.error(`Silme işlemi sırasında bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            // periods'u backend formatına çevir (string)
            const periods = (values.periods || []).map(p => ({
                dayOfWeek: p.dayOfWeek,
                startTime: p.startTime.format('HH:mm:ss'),
                endTime: p.endTime.format('HH:mm:ss')
            }));
            if (editingGroup) {
                await dispatch(updateGroup({
                    id: editingGroup.id,
                    name: values.name,
                    periods,
                    branchId: selectedBranch.id,
                    placeId: selectedPlace?.id
                })).unwrap();
                message.success('Grup başarıyla güncellendi!');
            } else {
                await dispatch(addGroup({
                    branchId: selectedBranch.id,
                    placeId: selectedPlace?.id,
                    name: values.name,
                    periods
                })).unwrap();
                message.success('Grup başarıyla eklendi!');
            }
            refetchGroups();
            setIsModalVisible(false);
            setEditingGroup(null);
        } catch (error) {
            console.error('Group operation error:', error);
            message.error(`İşlem sırasında bir hata oluştu: ${error.message || error}`);
        }
    };

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <div className="page-container">
                <Row gutter={[24, 24]}>
                    <Col xs={24}>
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <h3>Lütfen önce bir branş seçiniz</h3>
                            <p>Grupları yönetmek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="page-container">
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                                loading={loading}
                            >
                                Yeni Grup Ekle
                            </Button>
                        </Col>
                    </Row>
                </Col>
                <Col xs={24}>
                    <Table
                        columns={columns}
                        dataSource={groups}
                        rowKey="id"
                        scroll={{ x: true }}
                        loading={loading}
                        locale={{
                            emptyText: loading ? <Spin /> : 'Henüz grup bulunmamaktadır'
                        }}
                    />
                </Col>
                <Modal
                    title={editingGroup ? "Grubu Düzenle" : "Yeni Grup Ekle"}
                    open={isModalVisible}
                    onOk={handleModalOk}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setEditingGroup(null);
                    }}
                    confirmLoading={loading}
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Row gutter={[8, 8]}>
                            <Col xs={24}>
                                <Form.Item
                                    name="name"
                                    label="Grup Adı"
                                    rules={[{ required: true, message: 'Lütfen grup adını giriniz!' }]}
                                >
                                    <Input placeholder="Örn: A Grubu, B Grubu" />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.List name="periods">
                                    {(fields, { add, remove }) => (
                                        <>
                                            <label><b>Antrenman Saatleri</b></label>
                                            {fields.map(({ key, name, ...restField }) => (
                                                <Row gutter={8} key={key} align="top" style={{ marginBottom: 8 }}>
                                                    <Col span={8}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'dayOfWeek']}
                                                            rules={[{ required: true, message: 'Gün seçin' }]}
                                                        >
                                                            <Select options={dayOptions} placeholder="Gün" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'startTime']}
                                                            rules={[{ required: true, message: 'Başlangıç saati' }]}
                                                        >
                                                            <TimePicker format="HH:mm" minuteStep={5} placeholder="Başlangıç" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'endTime']}
                                                            rules={[{ required: true, message: 'Bitiş saati' }]}
                                                        >
                                                            <TimePicker format="HH:mm" minuteStep={5} placeholder="Bitiş" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={4} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', height: 40, padding: 0, minWidth: 48 }}>
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<MinusCircleOutlined style={{ fontSize: 22 }} />}
                                                            onClick={() => remove(name)}
                                                            shape="circle"
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        />
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Button type="dashed" onClick={() => add()} block>
                                                + Antrenman Saati Ekle
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Row>
        </div>
    );
};

export default Groups; 